import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {IBasketRepository} from '@/src/core/domain/repositories'
import {BasketError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {BasketData, BasketItem, SyncBasketItem} from '@/src/entities/basket/types'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

/**
 * Update Basket Item Use Case
 *
 * Orchestrates updating item quantity in the basket.
 * Handles both increase and decrease operations.
 * Contains business logic for quantity validation.
 *
 * @example
 * const result = await updateBasketItemUseCase.execute(basketItem, 3)
 * if (result.isOk()) {
 *   // Update UI with new basket data
 * }
 */
@injectable()
export class UpdateBasketItemUseCase {
  @inject(DI.IBasketRepository)
  basketRepository!: IBasketRepository

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  /**
   * Check if quantity is always available (unlimited stock)
   */
  private isAlwaysAvailable(item: BasketItem): boolean {
    return item.availability <= 0 && item.available
  }

  /**
   * Validate if the requested quantity is allowed
   */
  private validateQuantity(item: BasketItem, newQuantity: number): BasketError | null {
    if (newQuantity < 0) {
      return new BasketError('QUANTITY_EXCEEDED')
    }

    if (!this.isAlwaysAvailable(item) && newQuantity > item.availability) {
      return new BasketError('QUANTITY_EXCEEDED')
    }

    return null
  }

  async execute(
    item: BasketItem,
    newQuantity: number,
  ): Promise<OperationResult<BasketData, BasketError>> {
    // Validate quantity
    const validationError = this.validateQuantity(item, newQuantity)
    if (validationError) {
      return Result.fail(validationError)
    }

    const syncItem: SyncBasketItem = {
      id: item.id,
      product_id: item.product_id,
      variant_id: item.variant_id,
      quantity: newQuantity,
      amount: item.is_custom_price ? item.price : null,
    }

    try {
      const basketData = await this.basketRepository.sync([syncItem])
      return Result.ok(basketData)
    } catch (error) {
      this.errorReporter.capture(
        error instanceof Error ? error : new Error(String(error)),
        {item_id: item.id, new_quantity: newQuantity},
      )
      return Result.fail(new BasketError('SYNC_FAILED', error))
    }
  }

  /**
   * Convenience method to increase quantity by 1
   */
  async increase(item: BasketItem): Promise<OperationResult<BasketData, BasketError>> {
    return this.execute(item, item.quantity + 1)
  }

  /**
   * Convenience method to decrease quantity by 1
   */
  async decrease(item: BasketItem): Promise<OperationResult<BasketData, BasketError>> {
    return this.execute(item, item.quantity - 1)
  }
}
